import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { createLogger } from "../utils/logger";

const logger = createLogger("ErrorBoundary");

type ErrorBoundaryProps = {
  children: ReactNode;
  /** Optional fallback UI. If omitted, a default message is shown. */
  fallback?: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

/**
 * React Error Boundary to prevent the entire UI from crashing on unexpected render errors.
 *
 * Note: Error boundaries catch errors during rendering, in lifecycle methods, and in constructors
 * of the whole tree below them. They do NOT catch errors in event handlers or async code.
 */
class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: undefined,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep logging lightweight; production apps can wire this to a telemetry service.
    if (import.meta.env.MODE !== "prod") {
      logger.error("Unhandled UI error", error, info);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        style={{
          padding: 16,
          fontFamily:
            "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
        }}
      >
        <h2 style={{ margin: "0 0 8px 0" }}>Something went wrong</h2>
        <p style={{ margin: "0 0 12px 0" }}>
          An unexpected error occurred. Please refresh the page and try again.
        </p>
        <button type="button" onClick={this.handleReload}>
          Refresh
        </button>
        {import.meta.env.MODE !== "prod" && this.state.error?.message ? (
          <pre
            style={{
              marginTop: 12,
              padding: 12,
              background: "#f6f8fa",
              border: "1px solid #d0d7de",
              borderRadius: 6,
              whiteSpace: "pre-wrap",
            }}
          >
            {this.state.error.message}
          </pre>
        ) : null}
      </div>
    );
  }
}

export default ErrorBoundary;
